import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  exportSubmissionResponsesById,
  getSubmissionResponsesById,
} from "../../api/landingPageApi";
import PageLoader from "../../components/pageLoader";
import { DownloadIcon } from "@heroicons/react/outline";

const LandingPageSubmissions = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [responses, setResponses] = useState([]);

  const LIMIT = 20;

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const navigate = useNavigate();

  const totalPages = Math.ceil(total / LIMIT);

  const fetchResponses = async () => {
    try {
      setLoading(true);

      const offset = (page - 1) * LIMIT;

      const data = await getSubmissionResponsesById(id, LIMIT, offset);

      const list = Array.isArray(data) ? data : data.results || [];
      setTotal(data.count || 0);
      setResponses(list);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportSubmissionResponsesById(id);

      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;

      const fileName = `submissions_${new Date().toISOString().slice(0, 10)}.csv`;

      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setToast({
        type: "error",
        message: "Failed to export submissions",
      });
    }
  };

  useEffect(() => {
    fetchResponses();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-slate-400 hover:text-white"
            >
              ← Back to Landing Pages
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-white mt-2">
            Submissions
          </h1>
          <p className="text-sm text-slate-400">Landing Page ID: {id}</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExport}
            disabled={!responses.length}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-pink-600 rounded-lg text-white hover:bg-pink-700"
          >
            <DownloadIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between"></div>

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b text-left border-white/10 text-slate-400">
            <tr>
              <th className="px-6 py-3">Full Name</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Phone</th>
              <th className="px-6 py-3">City</th>
              <th className="px-6 py-3">Country</th>
              <th className="px-6 py-3">Created at</th>
            </tr>
          </thead>

          <tbody>
            {responses.map((item) => (
              <tr
                key={item.id}
                className="border-b border-white/5 hover:bg-slate-800/40 transition"
              >
                <td className="px-6 py-4 text-white">{item.full_name}</td>
                <td className="px-6 py-4 text-slate-300">{item.email}</td>
                <td className="px-6 py-4 text-slate-300">{item.phone}</td>
                <td className="px-6 py-4 text-slate-300">{item.geo_city}</td>
                <td className="px-6 py-4 text-slate-300">{item.geo_country}</td>
                <td className="px-6 py-4 text-slate-300">
                  {formatDate(item.created_at)}
                </td>
              </tr>
            ))}

            {!responses.length && (
              <tr>
                <td
                  colSpan={3}
                  className="px-6 py-10 text-center text-slate-400"
                >
                  No submissions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400">
            {/* Previous */}
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
            >
              Previous
            </button>

            {/* Pages */}
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => setPage(pageNumber)}
                    className={`px-3 py-1 rounded-md border border-white/10 transition
                ${
                  page === pageNumber
                    ? "bg-pink-600 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
                  >
                    {pageNumber}
                  </button>
                ),
              )}
            </div>

            {/* Next */}
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingPageSubmissions;
